import useSWR from "swr";

export interface SupportAccessGrantRow {
	id: string;
	actorUserId: number;
	subjectUserId: number;
	/** NULL means the subject's whole account, not a missing value. */
	companyId: number | null;
	reason: string;
	ticketRef: string | null;
	status: string;
	origin?: "consent" | "break_glass" | "override";
	/** Absent means read-only — see `useSupportAccessSession`. */
	mode?: "read" | "write";
	grantedAt: string;
	expiresAt: string;
}

export interface SupportAccessRequestRow {
	id: string;
	actorUserId: number;
	subjectUserId: number;
	/** NULL means the subject's whole account, not a missing value. */
	companyId: number | null;
	reason: string;
	ticketRef: string | null;
	requestedDurationMinutes: number;
	createdAt: string;
	expiresAt: string;
}

/**
 * The customer's own view of Equility staff access to THEIR company.
 *
 * Company-scoped rather than account-scoped on purpose: a firm Owner has to be
 * able to see and answer access to their client's books even when a colleague
 * is the subject. Filing any of this under a personal account page would hide
 * it from exactly the person accountable for it.
 */
export function useCompanySupportAccess(companyId?: number | string) {
	const base = companyId ? `companies/${companyId}/support-access` : null;

	const active = useSWR<SupportAccessGrantRow[]>(
		base ? `${base}/active` : null,
		// A live session is the state a customer most needs to be current about,
		// so this polls rather than waiting for a navigation.
		{ refreshInterval: 30_000, revalidateOnFocus: true },
	);

	const pending = useSWR<SupportAccessRequestRow[]>(
		base ? `${base}/requests` : null,
		{ refreshInterval: 60_000, revalidateOnFocus: true },
	);

	return {
		activeGrants: active.data ?? [],
		pendingRequests: pending.data ?? [],
		/** Distinguishes "asked and got none" from "could not ask". */
		isUnknown: Boolean(active.error) || (active.isLoading && !active.data),
		refresh: () => {
			active.mutate();
			pending.mutate();
		},
	};
}
